import { Channel } from '../Channel';
import { FieldType } from '../Field';
import { PivotNodeType, PivotRootLevel, } from './PivotNode';
export class PivotReduce {
    constructor(query, model, result) {
        this._query = query;
        this._model = model;
        this._result = result;
        this._reducers = new Map();
        this._rowNames = new Array();
        this._columnNames = new Array();
        this._valueFields = new Array();
        this.buildFields();
        this.buildReducers();
        this._reduce = this.buildReduce();
    }
    get query() {
        return this._query;
    }
    get model() {
        return this._model;
    }
    get result() {
        return this._result;
    }
    get reduce() {
        return this._reduce;
    }
    get reducers() {
        return this._reducers;
    }
    get rowNames() {
        return this._rowNames;
    }
    get columnNames() {
        return this._columnNames;
    }
    get valueFields() {
        return this._valueFields;
    }
    addRowNamesFields(fields) {
        for (const field of fields) {
            this.rowNames.push(field);
        }
    }
    addColumnNamesFields(fields) {
        for (const field of fields) {
            this.columnNames.push(field);
        }
    }
    addValueFields(fields) {
        for (const field of fields) {
            this.valueFields.push(field);
        }
    }
    buildFields() {
        this.addValueFields(this.query.values.fields);
        let rowMeasures = this.query.rows.measures;
        let columnMeasures = this.query.columns.measures;
        for (const [name, layer] of Array.from(this.query.rows.layers)) {
            rowMeasures = rowMeasures.concat(layer.fields.get(Channel.ROW));
        }
        for (const [name, layer] of Array.from(this.query.columns.layers)) {
            columnMeasures = columnMeasures.concat(layer.fields.get(Channel.COLUMN));
        }
        if (columnMeasures.length === 0) {
            this.addValueFields(rowMeasures.filter((f) => f.type === FieldType.VIEWCALC));
        }
        else if (rowMeasures.length === 0) {
            this.addValueFields(columnMeasures.filter((f) => f.type === FieldType.VIEWCALC));
        }
        this.addRowNamesFields(this.query.rows.fields.filter((f) => f.isNames));
        this.addColumnNamesFields(this.query.columns.fields.filter((f) => f.isNames));
    }
    buildReducers() {
        for (const [channel, layers] of Array.from(this.query.layers)) {
            for (const layer of layers) {
                this.reducers.set(layer.name, new LayerReducer(layer, this.model, this.result));
            }
        }
    }
    assignNames(fields, row) {
        if (this.valueFields.length === 0) {
            for (const field of fields) {
                row[field.alias] = 'No Measure Value';
            }
        }
        else {
            for (const field of fields) {
                for (const valueField of this.valueFields) {
                    row[field.alias] = this.model.getDefaultFieldName(valueField);
                }
            }
        }
    }
    reduceLayerRootNode(lrNode, path) {
        let nRow = null;
        const nRows = [];
        if (this.columnNames.length === 0) {
            nRow = {};
            this.assignNames(this.rowNames, nRow);
            nRows.push(nRow);
        }
        else if (this.rowNames.length === 0) {
            nRow = {};
            this.assignNames(this.columnNames, nRow);
            nRows.push(nRow);
        }
        else {
            for (const rowField of this.rowNames) {
                for (const columnField of this.columnNames) {
                    nRow = {};
                    for (const valueField of this.valueFields) {
                        nRow[rowField.alias] = this.model.getDefaultFieldName(valueField);
                        nRow[columnField.alias] = this.model.getDefaultFieldName(valueField);
                    }
                    nRows.push(nRow);
                }
            }
        }
        if (nRows.length === 0) {
            if (Object.keys(path).length > 0) {
                this.result.addBaseRow(Object.assign({}, path));
            }
        }
        else {
            for (const vRow of nRows) {
                if (Object.keys(vRow).length > 0 || Object.keys(path).length > 0) {
                    this.result.addBaseRow(Object.assign(vRow, path));
                }
            }
        }
    }
    buildReduce() {
        const path = {};
        let gStack = null;
        let gCurrNode = null;
        return () => {
            gStack = [];
            for (const [childValue, childNode] of Array.from(this.result.root.children)) {
                gStack.push(childNode);
            }
            while (gStack.length) {
                gCurrNode = gStack.pop();
                switch (gCurrNode.type) {
                    case PivotNodeType.DIMENSION:
                        path[gCurrNode.field.alias] = gCurrNode.value;
                        for (const [childValue, childNode] of Array.from(gCurrNode.children)) {
                            gStack.push(childNode);
                        }
                        break;
                    case PivotNodeType.NAMES:
                        for (const [childValue, childNode] of Array.from(gCurrNode.children)) {
                            gStack.push(childNode);
                        }
                        break;
                    case PivotNodeType.LAYER:
                        this.reducers.get(gCurrNode.name).reduce(gCurrNode);
                        break;
                    case PivotNodeType.ROOT:
                        if (gCurrNode.level === PivotRootLevel.LAYER) {
                            this.reduceLayerRootNode(gCurrNode, path);
                        }
                        for (const [childValue, childNode] of Array.from(gCurrNode.children)) {
                            gStack.push(childNode);
                        }
                        break;
                }
            }
        };
    }
}
export class LayerReducer {
    constructor(axis, model, result) {
        this._layer = axis;
        this._model = model;
        this._result = result;
        this._rowFields = new Array();
        this._columnFields = new Array();
        this._markFields = new Array();
        this._nameFields = new Array();
        this.buildFields();
        this._reduce = this.buildReduce();
    }
    get layer() {
        return this._layer;
    }
    get model() {
        return this._model;
    }
    get result() {
        return this._result;
    }
    get reduce() {
        return this._reduce;
    }
    get rowFields() {
        return this._rowFields;
    }
    get columnFields() {
        return this._columnFields;
    }
    get markFields() {
        return this._markFields;
    }
    get nameFields() {
        return this._nameFields;
    }
    addRowFields(fields) {
        for (const field of fields || this.layer.query.rows.measures) {
            this.rowFields.push(field);
        }
    }
    addColumnFields(fields) {
        for (const field of fields || this.layer.query.columns.measures) {
            this.columnFields.push(field);
        }
    }
    addNameFields(fields) {
        for (const field of fields || this.layer.query.values.fields) {
            this.nameFields.push(field);
        }
    }
    addRowNameFields() {
        for (const rowField of this.rowFields) {
            switch (rowField.type) {
                case 'VIEWCALC':
                    this.addNameFields(this.columnFields);
                    break;
                case 'VIEWVALUES':
                    if (this.columnFields.length === 0) {
                        this.addNameFields();
                    }
                    else {
                        for (const columnField of this.columnFields) {
                            this.addNameFields();
                        }
                    }
                    break;
            }
        }
    }
    addColumnNameFields() {
        for (const columnField of this.columnFields) {
            switch (columnField.type) {
                case 'VIEWCALC':
                    this.addNameFields(this.rowFields);
                    break;
                case 'VIEWVALUES':
                    if (this.rowFields.length === 0) {
                        this.addNameFields();
                    }
                    else {
                        for (const rowField of this.rowFields) {
                            this.addNameFields();
                        }
                    }
                    break;
            }
        }
    }
    buildFields() {
        for (const [channel, measures] of Array.from(this.layer.measures)) {
            for (const measure of measures) {
                switch (channel) {
                    case 'ROW':
                        this.rowFields.push(measure);
                        break;
                    case 'COLUMN':
                        this.columnFields.push(measure);
                        break;
                    default:
                        this.markFields.push(measure);
                }
            }
        }
        switch (this.layer.channel) {
            case 'ROW':
                this.addColumnFields();
                break;
            case 'COLUMN':
                this.addRowFields();
                break;
        }
        const valueFields = []
            .concat(this.rowFields, this.columnFields, this.markFields)
            .filter((f) => f.isValues);
        switch (this.layer.channel) {
            case '*':
                this.addNameFields();
                break;
            case 'ROW':
                if (this.columnFields.length === 0) {
                    if (valueFields.length > 0) {
                        this.addNameFields();
                    }
                    else {
                        this.addNameFields(this.rowFields);
                    }
                }
                else {
                    this.addRowNameFields();
                }
                break;
            case 'COLUMN':
                if (this.rowFields.length === 0) {
                    if (valueFields.length > 0) {
                        this.addNameFields();
                    }
                    else {
                        this.addNameFields(this.columnFields);
                    }
                }
                else {
                    this.addColumnNameFields();
                }
                break;
        }
    }
    assignValues(fields, mNode, row, mCurrField) {
        for (const field of fields) {
            switch (field.type) {
                case FieldType.VIEWCALC:
                    row[field.alias] = mNode.getResult(field);
                    break;
                case FieldType.VIEWVALUES:
                    if (mCurrField != null) {
                        row[field.alias] = mNode.getResult(mCurrField);
                    }
                    break;
            }
        }
    }
    reduceMeasureNode(lNode, mNode) {
        const path = {};
        let vRow = {};
        const mRows = [];
        let mCurrNode = mNode;
        mNode.reduce();
        if (this.nameFields.length === 0) {
            vRow['Measure Names'] = 'No Measure Value';
            this.assignValues(this.markFields, mNode, vRow);
            mRows.push(vRow);
        }
        else {
            for (const valueField of this.nameFields) {
                vRow = {};
                vRow['Measure Names'] = this.model.getDefaultFieldName(valueField);
                this.assignValues(this.rowFields, mNode, vRow, valueField);
                this.assignValues(this.columnFields, mNode, vRow, valueField);
                this.assignValues(this.markFields, mNode, vRow, valueField);
                mRows.push(vRow);
            }
        }
        while (mCurrNode) {
            switch (mCurrNode.type) {
                case PivotNodeType.DIMENSION:
                    path[mCurrNode.field.alias] = mCurrNode.value;
                    break;
                case PivotNodeType.NAMES:
                    for (const mRow of mRows) {
                        mRow[mCurrNode.field.alias] = mRow['Measure Names'];
                    }
                    break;
                case PivotNodeType.ROOT:
                    if (mCurrNode.level === PivotRootLevel.ROOT) {
                        for (const mRow of mRows) {
                            this.result.addLayerRow(lNode.name, Object.assign(mRow, path));
                        }
                    }
                    break;
            }
            mCurrNode = mCurrNode.parent;
        }
    }
    buildReduce() {
        return (layerNode) => {
            const lStack = [];
            let lCurrNode = null;
            for (const [childValue, childNode] of Array.from(layerNode.children)) {
                lStack.push(childNode);
            }
            while (lStack.length) {
                lCurrNode = lStack.pop();
                switch (lCurrNode.type) {
                    case PivotNodeType.DIMENSION:
                        for (const [childValue, childNode] of Array.from(lCurrNode.children)) {
                            lStack.push(childNode);
                        }
                        break;
                    case PivotNodeType.NAMES:
                        for (const [childValue, childNode] of Array.from(lCurrNode.children)) {
                            lStack.push(childNode);
                        }
                        break;
                    case PivotNodeType.MEASURE:
                        this.reduceMeasureNode(layerNode, lCurrNode);
                        break;
                }
            }
        };
    }
}
